import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { financeService, type PaymentMode, type PurchaseListItem } from '@/services/finance.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { formatINR } from '@/lib/currency';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

interface FormValues {
  payment_amount: string;
  payment_mode: string;
  transaction_id: string;
  payment_date: string;
  remark: string;
}

interface Props {
  purchase: PurchaseListItem;
  modes: PaymentMode[];
  onClose: () => void;
  onSuccess: () => void;
}

export function PaymentModal({ purchase, modes, onClose, onSuccess }: Props) {
  const queryClient = useQueryClient();
  const balance = Number(purchase.balance_amount);

  const { register, handleSubmit, reset, setValue, watch, control, formState: { errors } } = useForm<FormValues>({
    defaultValues: { payment_amount: '', payment_mode: '', transaction_id: '', payment_date: '', remark: '' },
  });

  useEffect(() => {
    reset({
      payment_amount: String(balance),
      payment_mode: modes[0]?.mode_name ?? '',
      transaction_id: '', payment_date: '', remark: '',
    });
  }, [purchase.id, balance, modes, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      financeService.recordPayment(purchase.id, {
        payment_amount: Number(values.payment_amount),
        payment_mode: values.payment_mode,
        transaction_id: values.transaction_id.trim(),
        payment_date: values.payment_date || null,
        remark: values.remark.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Payment recorded');
      void queryClient.invalidateQueries({ queryKey: ['finance', 'purchases'] });
      void queryClient.invalidateQueries({ queryKey: ['finance', 'vendors'] });
      void queryClient.invalidateQueries({ queryKey: ['finance', 'ledger'] });
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const paymentDate = watch('payment_date');

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>
            {purchase.vendor_name} · Invoice {purchase.invoice_number} · Balance {formatINR(balance)}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="p-amount">Payment Amount (₹) *</Label>
            <Input
              id="p-amount" type="number" min="0.01" step="0.01" max={balance}
              {...register('payment_amount', {
                required: 'Amount is required',
                validate: (val) => {
                  const n = Number(val);
                  if (!(n > 0)) return 'Enter a positive amount';
                  if (n > balance) return `Cannot exceed balance of ${formatINR(balance)}`;
                  return true;
                },
              })}
            />
            {errors.payment_amount && <p className="text-xs text-destructive">{errors.payment_amount.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="p-mode">Payment Mode *</Label>
            <Controller
              name="payment_mode" control={control} rules={{ required: 'Select a mode' }}
              render={({ field }) => (
                <Select value={field.value || undefined} onValueChange={field.onChange}>
                  <SelectTrigger id="p-mode"><SelectValue placeholder="Select mode" /></SelectTrigger>
                  <SelectContent>
                    {modes.map((m) => <SelectItem key={m.id} value={m.mode_name}>{m.mode_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.payment_mode && <p className="text-xs text-destructive">{errors.payment_mode.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="p-txn">Transaction ID *</Label>
            <Input id="p-txn" {...register('transaction_id', { required: 'Transaction ID is required' })} placeholder="UPI ref / bank ref / cheque no." />
            {errors.transaction_id && <p className="text-xs text-destructive">{errors.transaction_id.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="p-date">Payment Date</Label>
            <DatePicker id="p-date" value={paymentDate} onChange={(val) => setValue('payment_date', val)} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="p-remark">Remark</Label>
            <Textarea id="p-remark" {...register('remark')} rows={2} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Record Payment
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
